import { Component, type ErrorInfo, type ReactNode } from 'react'
import styles from '@/routes/ErrorPages.module.css'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Last line of defence around the whole tree.
 *
 * The router handles errors thrown inside a route; this catches anything above
 * it — a broken provider, a bad import — so the admin sees an explanation and a
 * way out instead of a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // No telemetry service is wired up; the console is what a developer has.
    console.error('Unhandled application error', error, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className={styles.page} role="alert">
        <div className={styles.panel}>
          <h1 className={styles.title}>The dashboard stopped unexpectedly</h1>
          <p className={styles.description}>
            {error.message || 'An unexpected error occurred.'} Reloading usually clears it.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primaryLink} onClick={() => window.location.assign('/')}>
              Reload the dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }
}
